import { Document, Packer, Paragraph, TextRun, ImageRun } from "docx";
import jsonConfig from "../../Config.json";
import { BlobServiceClient } from "@azure/storage-blob";
import { NotificationManager } from "react-notifications";

// Function to format date and time as dd-MMM-YYYY HH:mm AM/PM
const formatDateAndTime = (date) => {
    const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const hoursStr = hours.toString().padStart(2, '0');
    
    return `${day}-${month}-${year} ${hoursStr}:${minutes} ${ampm}`;
};

// Function to convert text signature to image
export const convertTextToSignatureImage = (text, options = {}) => {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Default options
        const {
            fontSize = 24,
            fontFamily = "Segoe Script, cursive",
            color = "#1e3a8a", 
            backgroundColor = "transparent",
            minWidth = 100,
            maxWidth = 500, 
            height = 80,
            padding = 20
        } = options;

        let currentFontSize = fontSize;
        let textWidth;
        let calculatedWidth;

        // Adjust font size if text is too long
        do {
            ctx.font = `${currentFontSize}px ${fontFamily}`;
            const textMetrics = ctx.measureText(text);
            textWidth = textMetrics.width;
            calculatedWidth = Math.max(minWidth, Math.min(maxWidth, textWidth + padding * 2));
            
            // If text is too wide and we haven't reached minimum font size, reduce font size
            if (textWidth + padding * 2 > maxWidth && currentFontSize > 16) {
                currentFontSize -= 2;
            } else {
                break;
            }
        } while (currentFontSize > 16);

        // Set canvas dimensions
        canvas.width = calculatedWidth;
        canvas.height = height;

        // Re-set font after canvas resize (canvas resize clears context)
        ctx.font = `${currentFontSize}px ${fontFamily}`;
        ctx.fillStyle = color;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // Set background
        if (backgroundColor !== "transparent") {
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = color; // Reset fill style for text
        }

        // Add some styling for signature effect
        ctx.shadowColor = "rgba(0, 0, 0, 0.1)";
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;

        // Draw the text
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        // Convert to blob
        canvas.toBlob((blob) => {
            if (blob) {
                const reader = new FileReader();
                reader.onload = (e) => resolve({
                    arrayBuffer: e.target.result,
                    blob: blob,
                    dataUrl: canvas.toDataURL("image/png")
                });
                reader.onerror = reject;
                reader.readAsArrayBuffer(blob);
            } else {
                reject(new Error("Failed to create signature image"));
            }
        }, "image/png");
    });
};

const processImageForDocx = (file) => {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();

        img.onload = () => {
            // Set canvas size
            canvas.width = 180;
            canvas.height = 40;

            // Draw image on canvas
            ctx.drawImage(img, 0, 0, 180, 40);

            // Convert to blob
            canvas.toBlob((blob) => {
                if (blob) {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target.result);
                    reader.onerror = reject;
                    reader.readAsArrayBuffer(blob);
                } else {
                    reject(new Error("Failed to create blob"));
                }
            }, "image/png");
        };

        img.onerror = reject;
        img.src = URL.createObjectURL(file);
    });
};

export const generateRTRDocument = async (candidateRequisitionData, candidateConsentData) => {
    let docChildren = [
        new Paragraph({
            children: [
                new TextRun({
                    text: "Right to Represent (RTR) Acknowledgment ",
                    font: "Arial",
                    size: 32,
                    bold: true,
                }),
            ],
            spacing: { after: 200 },
        }),

        new Paragraph({
            children: [
                new TextRun({
                    text: `Candidate Name: ${candidateRequisitionData?.name || "N/A"}`,
                    font: "Arial",
                    size: 18,
                }),
            ],
            spacing: { after: 200 },
        }),

        new Paragraph({
            children: [
                new TextRun({
                    text: `Email: ${candidateRequisitionData?.email || "N/A"}`,
                    font: "Arial",
                    size: 18,
                }),
            ],
            spacing: { after: 200 },
        }),

        new Paragraph({
            children: [
                new TextRun({
                    text: `Phone Number: ${candidateRequisitionData?.candidateResumeDetails
                        ?.countryCallingCode || " "
                        } ${candidateRequisitionData?.candidateResumeDetails?.phoneNumber ||
                        " "
                        }`,
                    font: "Arial",
                    size: 18,
                }),
            ],
            spacing: { after: 200 },
        }),

        new Paragraph({
            children: [
                new TextRun({
                    text: `Position Details :`,
                    font: "Arial",
                    size: 22,
                    bold: true,
                }),
            ],
            spacing: { after: 200 },
        }),

        new Paragraph({
            children: [
                new TextRun({
                    text: `Client: ${candidateRequisitionData?.clientDetails?.clientName || " "
                        }`,
                    font: "Arial",
                    size: 18,
                }),
            ],
            spacing: { after: 200 },
        }),
        
        candidateRequisitionData?.requisitionDetail?.endClientName && new Paragraph({
            children: [
                new TextRun({
                    text: `End Client: ${candidateRequisitionData?.requisitionDetail?.endClientName || " "
                        }`,
                    font: "Arial",
                    size: 18,
                }),
            ],  
            spacing: { after: 200 },
        }),

        new Paragraph({
            children: [
                new TextRun({
                    text: `Location: ${candidateRequisitionData?.requisitionDetail?.location[0] || " "
                        }`,
                    font: "Arial",
                    size: 18,
                }),
            ],
            spacing: { after: 200 },
        }),

        new Paragraph({
            children: [
                new TextRun({
                    text: `Primary Skill: ${candidateRequisitionData?.requisitionDetail?.primarySkill || " "
                        }`,
                    font: "Arial",
                    size: 18,
                }),
            ],
            spacing: { after: 200 },
        }),

        new Paragraph({
            children: [
                new TextRun({
                    text: `Experience Required: ${candidateRequisitionData?.requisitionDetail?.minExperience || " "
                        } to ${candidateRequisitionData?.requisitionDetail?.maxExperience || " "
                        } years`,
                    font: "Arial",
                    size: 18,
                }),
            ],
            spacing: { after: 200 },
        }),

        new Paragraph({
            children: [
                new TextRun({
                    text: `Acknowledgment by Candidate :`,
                    font: "Arial",
                    size: 22,
                    bold: true,
                }),
            ],
            spacing: { after: 200 },
        }),

        new Paragraph({
            children: [
                new TextRun({
                    text: `I, ${candidateRequisitionData?.name || ""
                        }, hereby acknowledge and confirm that I am applying for the position at ${candidateRequisitionData?.clientDetails?.clientName || "N/A"
                        } as detailed above.`,
                    font: "Arial",
                    size: 18,
                }),
            ],
            spacing: { after: 200 },
        }),

        new Paragraph({
            children: [
                new TextRun({
                    text: `I understand that by submitting this application, I am giving my consent to be represented by ClearedTalent for this position.`,
                    font: "Arial",
                    size: 18,
                }),
            ],
            spacing: { after: 200 },
        }),

        new Paragraph({
            children: [
                new TextRun({
                    text: `Signature:`,
                    font: "Arial",
                    size: 22,
                    bold: true,
                }),
            ],
            spacing: { after: 200 },
        }),
    ];

    if (candidateConsentData.signatureFile) {
        try {
            const imageBuffer = await processImageForDocx(
                candidateConsentData.signatureFile
            );
            docChildren.push(
                new Paragraph({
                    children: [
                        new ImageRun({
                            data: imageBuffer,
                            transformation: {
                                width: 180,
                                height: 40,
                            },
                            type: "png",
                        }),
                    ],
                    spacing: { after: 200 },
                })
            );
        } catch (err) {
            console.error("Image processing error:", err);
            NotificationManager.error("Failed to process signature image.","Error", 10000);
            return;
        }
    } else if (candidateConsentData.candidateSignatureText) {
        try {
            // Convert text signature to image
            const textSignatureImage = await convertTextToSignatureImage(
                candidateConsentData.candidateSignatureText,
                {
                    fontSize: 28,
                    fontFamily: "Segoe Script, cursive",
                    color: "#1e3a8a", // Blue ink pen color
                    minWidth: 80,
                    maxWidth: 500, // Increased for longer names
                    height: 70, // Slightly increased height
                    padding: 20
                }
            );
            
            docChildren.push(
                new Paragraph({
                    children: [
                        new ImageRun({
                            data: textSignatureImage.arrayBuffer,
                            transformation: {
                                width: 180,
                                height: 40,
                            },
                            type: "png",
                        }),
                    ],
                    spacing: { after: 200 },
                })
            );
        } catch (err) {
            console.error("Text signature processing error:", err);
            // Fallback to text if image generation fails
            docChildren.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: `${candidateConsentData.candidateSignatureText || "N/A"}`,
                            font: "Arial",
                            size: 18,
                        }),
                    ],
                    spacing: { after: 200 },
                })
            );
        }
    } else {
        docChildren.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: "N/A",
                        font: "Arial",
                        size: 18,
                    }),
                ],
                spacing: { after: 200 },
            })
        );
    }

    docChildren.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: `Date of Acknowledgment: ${formatDateAndTime(new Date())}`,
                    font: "Arial",
                    size: 18,
                }),
            ],
            spacing: { after: 200 },
        })
    );

    docChildren.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: `Recruiter Information:`,
                    font: "Arial",
                    size: 22,
                    bold: true,
                }),
            ],
            spacing: { after: 200 },
        })
    );

    docChildren.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: `Recruiter Name: ${candidateRequisitionData?.recruiterData?.displayName || "N/A"
                        }`,
                    font: "Arial",
                    size: 18,
                }),
            ],
            spacing: { after: 200 },
        })
    );

    docChildren.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: `Recruiter Email: ${candidateRequisitionData?.recruiterData?.email || "N/A"
                        }`,
                    font: "Arial",
                    size: 18,
                }),
            ],
            spacing: { after: 200 },
        })
    );

    try {
        const doc = new Document({
            creator: "ClearedTalent",
            title: "Application Confirmation",
            sections: [
                {
                    properties: {},
                    children: docChildren,
                },
            ],
        });

        const blob = await Packer.toBlob(doc);

        const fileName = `rtr_confirmation_${candidateConsentData?.candidateRequisitionId}.docx`;

        // Create File object
        const file = new File([blob], fileName, {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });

        return file;
    } catch (err) {
        console.error("Document generation error:", err);
    }
};

export const uploadRTRToBlobStorage = async ({ file, candidateRequisitionId }) => {
    if (!file) return null;
    const sas = jsonConfig.sasToken;
    const bsClient = new BlobServiceClient(
        `${jsonConfig.ctResourcesCdn + sas}`
    );
    const containerClient = bsClient.getContainerClient(
        "candidatereqattachment"
    );
    const blobName = `${candidateRequisitionId !== null ? candidateRequisitionId : "IdNotFound"
        }/rtr/${file.name}`;
    const blobClient = containerClient.getBlockBlobClient(blobName);
    try {
        await blobClient.uploadBrowserData(file);
        const url = `${jsonConfig.ctResourcesCdn}/candidatereqattachment/${blobName}`;
        return url;
    } catch (error) {
        return null;
    }
};